// frontend/app/components/admin/lessonAdminDraft.js
//
// The editable shape of one lesson, and the rules for turning it back into a
// save. Pure — no React, no axios — because these are the parts worth testing
// without a render, and because the editor should be about layout, not about
// what a valid lesson is.
//
// Validation here deliberately mirrors the server's own limits rather than
// trusting it to answer. A 400 after a long form is a bad way to learn that a
// title is too long, and the server's answer is still shown verbatim when one
// arrives — this only catches what can be caught before the round trip.

import { makeBlock, schemaFor, validateBlock } from "../lessonPlans/authoring/blockSchemas";

/** Statuses the server's LessonStatus literal accepts. */
export const LESSON_STATUSES = ["draft", "published", "archived"];

/**
 * Boards a lesson can be anchored to. Mirrors CURRICULA in
 * InternalAdminPanel.js, which is not exported — and adding an export is a
 * change to a file this work is not allowed to grow.
 */
export const LESSON_BOARDS = [
  "anatomy",
  "cbse",
  "igcse",
  "nyssls",
  "page",
  "saras",
  "ap",
  "ngss_hs",
];

const TITLE_MAX = 300;
const SUBTITLE_MAX = 500;
const DIFFICULTY_MAX = 32;
const MINUTES_MAX = 600;

const asList = (value) => (Array.isArray(value) ? value : []);
const asText = (value) => (value === null || value === undefined ? "" : String(value));

/** Server-managed fields, listed so a save never tries to move one. */
const STEP_FIELDS = [
  "step_key",
  "sequence",
  "phase",
  "instructional_mode",
  "title",
  "subtitle",
  "teacher_guidance",
  "student_activity",
  "estimated_minutes",
  "blocks",
  "completion_rules",
];

/**
 * The document as the form holds it. Text fields become strings so a null
 * subtitle and an emptied one are the same value going in and coming out;
 * numbers stay text because that is what a TextInput gives back.
 *
 * Everything the server sent is spread in first, including fields this file
 * has never heard of. The save is a full replacement, so anything the draft
 * fails to carry is a field the save cannot echo — and a field the save does
 * not echo is deleted. Carrying by default and naming only the exceptions puts
 * the burden on the field that must *not* survive, which is the rarer case.
 */
export const toDraft = (document) => {
  const doc = document || {};
  return {
    ...doc,

    id: doc.id ?? null,
    slug: asText(doc.slug),
    base_board: asText(doc.base_board),
    topic_name: asText(doc.topic_name),
    subject_name: asText(doc.subject_name),
    board_topic_id: doc.board_topic_id ?? null,
    source_topic_id: doc.source_topic_id ?? null,
    version: doc.version ?? null,
    content_locked: Boolean(doc.content_locked),
    content_locked_by: asText(doc.content_locked_by),
    content_locked_at: doc.content_locked_at || null,

    title: asText(doc.title),
    subtitle: asText(doc.subtitle),
    description: asText(doc.description),
    lesson_no: asText(doc.lesson_no ?? 1),
    duration_minutes: asText(doc.duration_minutes ?? ""),
    difficulty: asText(doc.difficulty),
    status: asText(doc.status) || "published",

    objectives: asList(doc.objectives).map(asText),
    prerequisites: asList(doc.prerequisites).map(asText),
    teacher_tips: asList(doc.teacher_tips).map(asText),

    // Carried, not edited. Nothing renders `materials` or `resource_shelf` any
    // more — the toolkit shelf and the workflow columns were their only
    // readers — so the form does not offer them. They are still held here
    // because the save replaces the whole document: a field left out of the
    // payload is cleared, not kept, so dropping them would delete authored
    // content the moment an admin fixed a typo somewhere else.
    materials: asList(doc.materials).map(asText),
    resource_shelf:
      doc.resource_shelf && typeof doc.resource_shelf === "object"
        ? { ...doc.resource_shelf }
        : {},

    concepts: asList(doc.concepts).map((concept) => ({ ...concept })),
    steps: asList(doc.steps).map((step, index) => ({
      ...step,
      sequence: step?.sequence ?? index + 1,
      blocks: asList(step?.blocks).map((block) => ({ ...block })),
    })),
  };
};

/** "" from an emptied numeric field means "no value", not zero. */
const toNumberOrNull = (value) => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

const isBlankOrWholeNumberIn = (value, min, max) => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return true;
  const parsed = Number(trimmed);
  return Number.isInteger(parsed) && parsed >= min && parsed <= max;
};

/** Rows an author added and left empty are dropped, not saved as blanks. */
const cleanList = (items) => asList(items).map((item) => asText(item).trim()).filter(Boolean);

/** Fields this function names for itself, below. */
const EMITTED_FIELDS = [
  "title",
  "subtitle",
  "description",
  "lesson_no",
  "duration_minutes",
  "difficulty",
  "status",
  "objectives",
  "prerequisites",
  "teacher_tips",
  "materials",
  "resource_shelf",
  "concepts",
  "steps",
];

/**
 * Fields a save must never send, whatever the draft is holding.
 *
 * `slug` is what the seeder matches a file to a row on and `base_board` picks
 * the content database this lesson's 3D scenes resolve against, so neither is
 * safe to edit from a content screen. `schema_version` decides which phase
 * vocabulary the server accepts, and omitting it keeps what is stored rather
 * than silently promoting a v1 lesson to the Loop. The rest are the server's
 * own bookkeeping — the row id, the version counter, the content lock, the
 * timestamps — which a content save does not get a vote on.
 */
const SERVER_OWNED_FIELDS = [
  "id",
  "slug",
  "base_board",
  "schema_version",
  "version",
  "content_locked",
  "content_locked_at",
  "content_locked_by",
  "created_at",
  "updated_at",
  "owner_email",
];

const NOT_CARRIED = new Set([...EMITTED_FIELDS, ...SERVER_OWNED_FIELDS]);

/**
 * Everything else the document arrived with, echoed back untouched.
 *
 * The PUT assigns every field it models straight from the body, so a field the
 * payload leaves out is written as null rather than left alone. That is how
 * `board_topic_id` and `source_topic_id` — the two ids every 3D scene and video
 * segment in the lesson resolves against — used to be erased by an admin fixing
 * a typo, along with `topic_name`, `subject_name`, `chapter_id` and
 * `chapter_name`.
 *
 * Carried by exclusion rather than by name so the next field added to the
 * document survives an admin's first save without anyone remembering to come
 * back here. Extra keys are harmless: AdminLessonSaveIn ignores what it does
 * not model.
 */
const carriedFields = (draft) =>
  Object.fromEntries(
    Object.entries(draft || {}).filter(([key]) => !NOT_CARRIED.has(key))
  );

/**
 * The save body: the whole editable document, not a sparse patch. The server
 * replaces concepts and steps outright from what arrives, so an omitted list
 * is a request to clear it — which also means this must never be trimmed to
 * "only what changed".
 *
 * `phase` is passed through untouched, for the same reason `schema_version` is
 * withheld: the vocabulary a step speaks is decided by the lesson, not here.
 */
export const toPayload = (draft) => {
  const d = draft || {};
  return {
    ...carriedFields(d),

    title: asText(d.title).trim(),
    subtitle: asText(d.subtitle).trim() || null,
    description: asText(d.description).trim() || null,
    lesson_no: toNumberOrNull(d.lesson_no) ?? 1,
    duration_minutes: toNumberOrNull(d.duration_minutes),
    difficulty: asText(d.difficulty).trim() || null,
    status: d.status || "published",

    objectives: cleanList(d.objectives),
    prerequisites: cleanList(d.prerequisites),
    teacher_tips: cleanList(d.teacher_tips),

    // Echoed back exactly as they arrived. See toDraft: these are unread by
    // any screen today, and omitting them from a full-replace save would
    // delete them rather than leave them alone.
    materials: asList(d.materials).map(asText),
    resource_shelf: d.resource_shelf && typeof d.resource_shelf === "object" ? d.resource_shelf : {},

    concepts: asList(d.concepts).map((concept, index) => ({
      concept_key: asText(concept?.concept_key),
      label: asText(concept?.label),
      objective_index: concept?.objective_index ?? null,
      sequence: concept?.sequence ?? index,
    })),

    steps: asList(d.steps).map((step, index) => {
      const out = {};
      STEP_FIELDS.forEach((field) => {
        if (step?.[field] !== undefined) out[field] = step[field];
      });
      // Sequence is what orders the lesson; the array order is what the admin
      // sees, so the array wins over whatever number came back.
      out.sequence = index + 1;
      out.blocks = asList(step?.blocks);
      out.completion_rules = asList(step?.completion_rules);
      out.subtitle = asText(step?.subtitle).trim() || null;
      out.teacher_guidance = asText(step?.teacher_guidance).trim() || null;
      out.student_activity = asText(step?.student_activity).trim() || null;
      out.estimated_minutes = toNumberOrNull(step?.estimated_minutes);
      return out;
    }),
  };
};

/** Everything wrong with the draft, in the order an admin would meet it. */
export const validateDraft = (draft) => {
  const d = draft || {};
  const problems = [];

  const title = asText(d.title).trim();
  if (!title) problems.push("Title is required.");
  else if (title.length > TITLE_MAX)
    problems.push(`Title is ${title.length} characters; the limit is ${TITLE_MAX}.`);

  if (asText(d.subtitle).trim().length > SUBTITLE_MAX)
    problems.push(`Subtitle is longer than ${SUBTITLE_MAX} characters.`);

  if (asText(d.difficulty).trim().length > DIFFICULTY_MAX)
    problems.push(`Difficulty is longer than ${DIFFICULTY_MAX} characters.`);

  const lessonNo = Number(String(d.lesson_no ?? "").trim());
  if (!Number.isInteger(lessonNo) || lessonNo < 1)
    problems.push("Lesson number must be a whole number of 1 or more.");

  if (!isBlankOrWholeNumberIn(d.duration_minutes, 0, MINUTES_MAX))
    problems.push(`Duration must be a whole number of minutes between 0 and ${MINUTES_MAX}.`);

  if (!LESSON_STATUSES.includes(d.status))
    problems.push(`Status must be one of: ${LESSON_STATUSES.join(", ")}.`);

  // The save replaces the whole set of steps, so an empty list is a request to
  // delete every step — which the server refuses outright.
  if (!asList(d.steps).length) problems.push("A lesson must keep at least one step.");

  const declaredConcepts = new Set(
    asList(d.concepts)
      .map((concept) => asText(concept?.concept_key).trim())
      .filter(Boolean)
  );
  // Block ids must be unique across the whole lesson, not per step, and a
  // completion rule may only name a block declared before it.
  const seenBlockIds = new Set();

  asList(d.steps).forEach((step, index) => {
    const label = `Step ${index + 1}`;
    if (!asText(step?.title).trim()) problems.push(`${label} needs a title.`);
    else if (asText(step.title).trim().length > TITLE_MAX)
      problems.push(`${label} title is longer than ${TITLE_MAX} characters.`);
    // step_key is what student progress is keyed on. It is not editable here
    // for that reason — renaming one orphans every answer recorded against it
    // — so a step without one came from bad data upstream.
    if (!asText(step?.step_key).trim()) problems.push(`${label} has no step key.`);
    if (!isBlankOrWholeNumberIn(step?.estimated_minutes, 0, MINUTES_MAX))
      problems.push(`${label} minutes must be between 0 and ${MINUTES_MAX}.`);

    asList(step?.blocks).forEach((block, blockIndex) => {
      const where = `${label}, block ${blockIndex + 1}`;
      const id = asText(block?.id).trim();
      if (!id) problems.push(`${where} has no id.`);
      else if (seenBlockIds.has(id))
        problems.push(`${where} reuses the block id "${id}"; ids must be unique in a lesson.`);
      else seenBlockIds.add(id);

      const conceptKey = asText(block?.concept_key).trim();
      if (conceptKey && !declaredConcepts.has(conceptKey))
        problems.push(`${where} names concept "${conceptKey}", which this lesson does not declare.`);

      // The server would take a block whose fields are all still empty: the
      // seeder only asks for a unique id and a concept that resolves. That is
      // the wrong bar for a block someone just added from the palette, which
      // arrives with nothing filled in and would reach students as a blank
      // question. BlockEditor already lists these against the block itself,
      // but a step can be collapsed and Save is at the top of the page, so
      // they are repeated here where the save is refused.
      //
      // The block's own concept key is declared for this call only, so the
      // check above is not answered twice in two different wordings.
      const typeLabel = schemaFor(block?.type)?.label;
      validateBlock(block, {
        conceptKeys: conceptKey ? [...declaredConcepts, conceptKey] : [...declaredConcepts],
      }).forEach((problem) => {
        problems.push(`${where}${typeLabel ? ` (${typeLabel})` : ""}: ${problem}.`);
      });
    });

    asList(step?.completion_rules).forEach((rule) => {
      const blockId = asText(rule?.block_id).trim();
      if (blockId && !seenBlockIds.has(blockId))
        problems.push(
          `${label} has a completion rule for block "${blockId}", which no block up to here declares.`
        );
    });
  });

  return problems;
};

/** A new draft with `fields` applied to one step. Never mutates. */
export const patchStep = (draft, stepIndex, fields) => ({
  ...draft,
  steps: asList(draft?.steps).map((step, i) =>
    i === stepIndex ? { ...step, ...fields } : step
  ),
});

/** A new draft with one step's blocks replaced. Never mutates. */
export const setStepBlocks = (draft, stepIndex, blocks) =>
  patchStep(draft, stepIndex, { blocks });

export const patchBlock = (draft, stepIndex, blockIndex, nextBlock) =>
  setStepBlocks(
    draft,
    stepIndex,
    asList(draft?.steps?.[stepIndex]?.blocks).map((block, i) =>
      i === blockIndex ? nextBlock : block
    )
  );

/** Every block id in use anywhere in the lesson. */
const lessonBlockIds = (draft) =>
  asList(draft?.steps).flatMap((step) =>
    asList(step?.blocks).map((block) => asText(block?.id))
  );

/**
 * A new block of `type`, appended to one step. Never mutates; an unknown type
 * returns the draft it was given.
 *
 * The id is drawn against the whole lesson rather than against the step, which
 * is the only part of this worth being careful about. The seeder refuses a
 * document that uses one block id twice *anywhere* in it, so a
 * `multiple_choice-1` already sitting in Wonder is enough to make a second one
 * added to Apply unsaveable — and the message an admin would get back names a
 * block they cannot see from the step they are editing.
 */
export const addBlock = (draft, stepIndex, type) => {
  const step = asList(draft?.steps)[stepIndex];
  if (!step) return draft;

  const block = makeBlock(type, lessonBlockIds(draft));
  if (!block) return draft;

  return setStepBlocks(draft, stepIndex, [...asList(step.blocks), block]);
};

export const removeBlock = (draft, stepIndex, blockIndex) =>
  setStepBlocks(
    draft,
    stepIndex,
    asList(draft?.steps?.[stepIndex]?.blocks).filter((_, i) => i !== blockIndex)
  );

export const moveBlock = (draft, stepIndex, blockIndex, delta) => {
  const blocks = asList(draft?.steps?.[stepIndex]?.blocks);
  const target = blockIndex + delta;
  if (target < 0 || target >= blocks.length) return draft;
  const next = [...blocks];
  next[blockIndex] = blocks[target];
  next[target] = blocks[blockIndex];
  return setStepBlocks(draft, stepIndex, next);
};

/**
 * Whether the draft differs from what was loaded. Compared on the save body so
 * that trimming a trailing space, or deleting an empty objective row, does not
 * count as an edit — neither would change anything the server stores.
 */
export const hasUnsavedChanges = (draft, baseline) =>
  JSON.stringify(toPayload(draft)) !== JSON.stringify(toPayload(baseline));
